import { AnimationEvent } from '@angular/animations';
import { DomPortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import {
  ApplicationRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { dynamicAnimations } from 'app/shared/animations/animations';
import { HttpLoaderIndicatorService } from 'app/shared/layout/services/http-loader-indicator.service';

@Component({
  selector: 'shared-popup',
  templateUrl: './popup.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [dynamicAnimations],
  standalone: false,
})
export class PopupComponent implements OnDestroy, OnChanges {
  @ViewChild('popupTemplate', { static: true })
  public popupTemplate?: TemplateRef<any>;

  @Input()
  set visible(val) {
    this._visible = val;
    this.visibleChange.emit(val);
  }
  get visible(): boolean {
    return this._visible;
  }

  @Input() outsideClick = false;
  @Input() panelClass = 'popup';
  @Input() animation: 'none' | 'fromRight' | 'scale' | 'exit' = 'none';

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() animationEnd = new EventEmitter<boolean>();
  @Output() animationStart = new EventEmitter<boolean>();

  customPopupTemplate?: TemplateRef<any>;

  _visible = false;

  portalHost?: DomPortalOutlet;
  popupAnimation = this.animation;

  activeClass = false;
  isLoading = this.loadingService.loading$;

  constructor(
    private ref: ChangeDetectorRef,
    private appRef: ApplicationRef,
    private injector: Injector,
    private viewContainerRef: ViewContainerRef,
    private loadingService: HttpLoaderIndicatorService
  ) {}

  @HostListener('document:click', ['$event'])
  clickEvent(event: MouseEvent): void {
    if (this.outsideClick && this.visible) {
      const clickedOutside = (event.target as HTMLElement).classList.contains('popup');
      if (clickedOutside) {
        this.startExitAnimation();
      }
    }
  }

  removeComponentFromBody(): void {
    if (this.portalHost) {
      this.portalHost.detach();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['visible'];
    if (change && change.previousValue !== change.currentValue) {
      if (this.visible) {
        this.createDomElement();
      } else if (this.portalHost) {
        this.startExitAnimation();
      }
    }
  }

  ngOnDestroy(): void {
    this.removeComponentFromBody();
  }

  onAnimationDone(event: any): void {
    if (event.toState === 'exit') {
      this.removeComponentFromBody();
    }
    this.animationEnd.emit(true);
  }

  onAnimationStart(event: AnimationEvent): void {
    this.activeClass = event.toState !== 'exit';
    this.animationStart.emit(true);
  }

  startExitAnimation(): void {
    this.popupAnimation = 'exit';
    this.activeClass = false;
    this.visible = false;
    this.ref.markForCheck();
  }

  private createDomElement(): void {
    this.popupAnimation = this.animation;
    // Create a portalHost from a DOM element
    this.portalHost = new DomPortalOutlet(document.body, this.appRef, this.injector);

    // Create a template portal
    if (!this.popupTemplate) {
      return;
    }
    const templatePortal = new TemplatePortal(this.popupTemplate, this.viewContainerRef);

    // Attach portal to host
    this.portalHost.attach(templatePortal);
  }
}
